import { useCallback } from 'react';
import { useAuth } from './useAuth.ts';
import type { CompactPuzzle } from '@ricochet/game-engine';
import * as puzzlesApi from '../api/puzzles.ts';
import * as usersApi from '../api/users.ts';
import * as leaderboardApi from '../api/leaderboard.ts';
import * as speedrunApi from '../api/speedrun.ts';
import * as achievementsApi from '../api/achievements.ts';
import * as endlessApi from '../api/endless.ts';

export function useApi() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth();

  const getToken = useCallback(async () => {
    if (!isAuthenticated) return undefined;
    return getAccessTokenSilently();
  }, [isAuthenticated, getAccessTokenSilently]);

  const listPuzzles = useCallback(
    async (params?: Parameters<typeof puzzlesApi.listPuzzles>[0]) => {
      const token = await getToken();
      return puzzlesApi.listPuzzles(params, token);
    },
    [getToken],
  );

  const uploadPuzzle = useCallback(
    async (data: Parameters<typeof puzzlesApi.uploadPuzzle>[0]) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return puzzlesApi.uploadPuzzle(data, token);
    },
    [getToken],
  );

  const toggleLike = useCallback(
    async (puzzleId: string) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return puzzlesApi.toggleLike(puzzleId, token);
    },
    [getToken],
  );

  const submitSolve = useCallback(
    async (puzzleId: string, moveSequence: string) => {
      const token = await getToken();
      return puzzlesApi.submitSolve(puzzleId, moveSequence, token);
    },
    [getToken],
  );

  const getMe = useCallback(
    async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return usersApi.getMe(token);
    },
    [getToken],
  );

  const updateMe = useCallback(
    async (data: { username?: string; country?: string; avatarId?: string | null; playerCardDecoration?: string }) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return usersApi.updateMe(data, token);
    },
    [getToken],
  );

  const getMyLikes = useCallback(
    async (params?: Parameters<typeof puzzlesApi.getMyLikes>[0]) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return puzzlesApi.getMyLikes(params, token);
    },
    [getToken],
  );

  const getPuzzleSolves = useCallback(
    async (puzzleId: string, params?: { page?: number; limit?: number }) => {
      const token = await getToken();
      return puzzlesApi.getPuzzleSolves(puzzleId, params, token);
    },
    [getToken],
  );

  const getUser = useCallback(
    async (id: string) => usersApi.getUser(id),
    [],
  );

  const getEloLeaderboard = useCallback(
    async (params?: { limit?: number; page?: number }) => {
      const token = await getToken();
      return leaderboardApi.getEloLeaderboard(params, token);
    },
    [getToken],
  );

  const getCommunityLeaderboard = useCallback(
    async (params?: { limit?: number; page?: number }) => {
      const token = await getToken();
      return leaderboardApi.getCommunityLeaderboard(params, token);
    },
    [getToken],
  );

  const getStreakLeaderboard = useCallback(
    async (params?: { limit?: number; page?: number }) => {
      const token = await getToken();
      return leaderboardApi.getStreakLeaderboard(params, token);
    },
    [getToken],
  );

  const initSpeedrun = useCallback(
    async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return speedrunApi.initSpeedrun(token);
    },
    [getToken],
  );

  const bindSpeedrun = useCallback(
    async (initToken: string, puzzles: CompactPuzzle[]) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return speedrunApi.bindSpeedrun(token, initToken, puzzles);
    },
    [getToken],
  );

  const startSpeedrun = useCallback(
    async (puzzles: CompactPuzzle[]) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return speedrunApi.startSpeedrun(puzzles, token);
    },
    [getToken],
  );

  const finishSpeedrun = useCallback(
    async (sessionToken: string, puzzles: CompactPuzzle[], solutions: string[], puzzleTimes: number[]) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return speedrunApi.finishSpeedrun(token, sessionToken, puzzles, solutions, puzzleTimes);
    },
    [getToken],
  );

  const getSpeedrunLeaderboard = useCallback(
    async (params?: { limit?: number; page?: number }) => {
      const token = await getToken();
      return speedrunApi.getSpeedrunLeaderboard(params, token);
    },
    [getToken],
  );

  const getMyAchievements = useCallback(
    async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return achievementsApi.getMyAchievements(token);
    },
    [getToken],
  );

  const notifyDiscordJoin = useCallback(
    async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return achievementsApi.notifyDiscordJoin(token);
    },
    [getToken],
  );

  const getPuzzle = useCallback(
    async (id: string) => {
      const token = await getToken();
      return puzzlesApi.getPuzzle(id, token);
    },
    [getToken],
  );

  const submitEndlessSolve = useCallback(
    async (data: Parameters<typeof endlessApi.submitEndlessSolve>[0]) => {
      const token = await getToken();
      if (!token) return;
      return endlessApi.submitEndlessSolve(data, token);
    },
    [getToken],
  );

  return { listPuzzles, uploadPuzzle, toggleLike, submitSolve, getPuzzleSolves, getMyLikes, getPuzzle, getMe, updateMe, getUser, getEloLeaderboard, getCommunityLeaderboard, getStreakLeaderboard, initSpeedrun, bindSpeedrun, startSpeedrun, finishSpeedrun, getSpeedrunLeaderboard, getMyAchievements, notifyDiscordJoin, submitEndlessSolve };
}
